/**
 * KnowledgeBaseTab — Browse documents in AskAldoKB / index sources,
 * view document changes (who changed what, when), and blob-to-SharePoint copy utility.
 * Merges the former SharePointTab and document-changes mode of ActivityTab.
 */

import React, { useState, useEffect, useCallback } from 'react';

import {
  FolderOpen, RefreshCw, CheckCircle, AlertTriangle, FileText, Folder,
  ArrowLeft, Search, FileSpreadsheet, FileType, Globe, Loader2,
  Bot, Copy, ChevronRight, MapPin, ExternalLink, Upload, HardDrive,
  Image, ShieldAlert, Activity, Database, Zap, Cloud, Settings,
  ChevronDown, GitBranch, ArrowLeftRight, RotateCcw, Plus, Clock, XCircle, MessageSquare
} from 'lucide-react';
import type { IndexConfig, ActivityEntry, RegistryAgent, ApprovalBatch } from './types';
import { API_BASE, authFetch, AGENT_BLOB_MAPPING, getAgentDisplayName, getAgentColor, formatRelativeTime } from './constants';
import ChangeDetectionPanel from './ChangeDetectionPanel';
import IndexTestPanel from './IndexTestPanel';
import { IMAGE_MODE_OPTIONS, getResolvedImageModeOptions, isVisionHeavy, captionToImageMode, type ImageModeChoice } from './imageModeOptions';

interface KnowledgeBaseTabProps {
  indexes: IndexConfig[];
  selectedEnvironment: string;
  registryAgents?: RegistryAgent[];
  syncingIndex?: string | null;
  onSync?: (indexId: string, mode: 'incremental' | 'full', imageMode?: string, maxVisionCalls?: number) => void;
  onRefreshIndexes?: () => void;
}

// Per-index overrides persist in localStorage so a chosen test strategy
// (and its vision-call cap) survives a page reload while you iterate —
// AA-220's whole point is testing a strategy without a code deploy.
const LS_IMAGE_MODE_PREFIX = 'askaldo:idxAdmin:imageMode:';
const LS_MAX_VISION_PREFIX = 'askaldo:idxAdmin:maxVisionCalls:';

const KnowledgeBaseTab: React.FC<KnowledgeBaseTabProps> = ({
  indexes, selectedEnvironment, registryAgents = [],
  syncingIndex = null, onSync, onRefreshIndexes
}) => {
  // Per-index image_mode choice, pre-seeded from localStorage, then from the
  // index's current setting.
  const [captionChoice, setCaptionChoice] = useState<Record<string, ImageModeChoice>>({});
  const [maxVisionCalls, setMaxVisionCalls] = useState<Record<string, string>>({});

  const captionFor = (idx: IndexConfig): ImageModeChoice => {
    if (captionChoice[idx.id]) return captionChoice[idx.id];
    try {
      const stored = window.localStorage.getItem(LS_IMAGE_MODE_PREFIX + idx.id);
      if (stored) return stored as ImageModeChoice;
    } catch { /* localStorage unavailable (private mode, SSR, etc.) */ }
    const current = (idx.processing?.image_handling || '').toLowerCase();
    if (!current) return 'default';
    if (current === 'off' || current === 'none') return 'skip';
    return (IMAGE_MODE_OPTIONS.some(o => o.value === current) ? current : 'caption') as ImageModeChoice;
  };
  const maxVisionCallsFor = (idx: IndexConfig): string => {
    if (maxVisionCalls[idx.id] !== undefined) return maxVisionCalls[idx.id];
    try {
      return window.localStorage.getItem(LS_MAX_VISION_PREFIX + idx.id) || '';
    } catch {
      return '';
    }
  };
  const setChoiceFor = (indexId: string, choice: ImageModeChoice) => {
    setCaptionChoice(prev => ({ ...prev, [indexId]: choice }));
    try { window.localStorage.setItem(LS_IMAGE_MODE_PREFIX + indexId, choice); } catch { /* ignore */ }
  };
  const setMaxVisionCallsFor = (indexId: string, value: string) => {
    setMaxVisionCalls(prev => ({ ...prev, [indexId]: value }));
    try {
      if (value) window.localStorage.setItem(LS_MAX_VISION_PREFIX + indexId, value);
      else window.localStorage.removeItem(LS_MAX_VISION_PREFIX + indexId);
    } catch { /* ignore */ }
  };
  // Parse the free-text cap input to a non-negative integer, or undefined
  // for "no cap" (blank / invalid). 0 is a legitimate value.
  const parseVisionCap = (raw: string): number | undefined => {
    if (raw === '') return undefined;
    const n = Number(raw);
    return Number.isFinite(n) && Number.isInteger(n) && n >= 0 ? n : undefined;
  };
  const [kbMode, setKbMode] = useState<'browse' | 'sources' | 'changes' | 'activity' | 'copy'>('browse');
  const [kbItems, setKbItems] = useState<any[]>([]);
  const [kbLoading, setKbLoading] = useState(false);
  const [kbPath, setKbPath] = useState('');
  const [kbError, setKbError] = useState<string | null>(null);
  const [kbSiteInfo, setKbSiteInfo] = useState<any>(null);
  // Copy utility state
  const [copyAgent, setCopyAgent] = useState<string>('');
  const [copyContainer, setCopyContainer] = useState<string>('');
  const [copyDryRun, setCopyDryRun] = useState<any>(null);
  const [copyRunning, setCopyRunning] = useState(false);
  const [copyResult, setCopyResult] = useState<any>(null);
  // Document changes state
  const [activities, setActivities] = useState<ActivityEntry[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState<string | null>(null);
  const [siteFilter, setSiteFilter] = useState<string>('askaldokb');

  // Index Sources — expanded rows, sample data, alias management
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);
  const [sampleData, setSampleData] = useState<Record<string, any>>({});
  const [loadingSample, setLoadingSample] = useState<string | null>(null);
  const [aliases, setAliases] = useState<any[]>([]);
  const [aliasLoading, setAliasLoading] = useState(false);
  const [aliasAction, setAliasAction] = useState<string | null>(null);
  const [showAliasPanel, setShowAliasPanel] = useState(false);
  const [newAliasName, setNewAliasName] = useState('');
  const [newAliasTarget, setNewAliasTarget] = useState('');

  // Reconcile + inline test state
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileResult, setReconcileResult] = useState<any>(null);
  const [testingBatch, setTestingBatch] = useState<ApprovalBatch | null>(null);

  // ---- Index Sources: resolve agent → live index data ----
  // Build a lookup from index name → live IndexConfig
  const indexByName = new Map<string, IndexConfig>();
  for (const idx of indexes) {
    indexByName.set(idx.id, idx);
    if (idx.azure_search?.id) indexByName.set(idx.azure_search.id, idx);
    if (idx.pinecone?.id) indexByName.set(idx.pinecone.id, idx);
  }

  // For each agent, resolve the index name for the current env
  const resolveIndexName = (agent: RegistryAgent): string | null => {
    const kb = agent.knowledgeBase;
    if (!kb) return null;
    // Prefer per-env mapping
    if (kb.environments?.[selectedEnvironment]) return kb.environments[selectedEnvironment];
    // Fall back to template
    if (kb.indexName) return kb.indexName.replace(/\{env\}/g, selectedEnvironment);
    return null;
  };

  // Match agent to live index data
  const getAgentIndex = (agent: RegistryAgent): IndexConfig | null => {
    const name = resolveIndexName(agent);
    if (!name) return null;
    // Exact match first
    if (indexByName.has(name)) return indexByName.get(name)!;
    // Partial match (index id often contains the name)
    for (const idx of indexes) {
      if (idx.id.includes(name) || name.includes(idx.id)) return idx;
    }
    return null;
  };

  // Build index → alias lookup
  const indexToAliases: Record<string, any[]> = {};
  for (const alias of aliases) {
    for (const idx of alias.indexes || []) {
      if (!indexToAliases[idx]) indexToAliases[idx] = [];
      indexToAliases[idx].push(alias);
    }
  }

  // ---- Alias management ----
  const fetchAliases = useCallback(async () => {
    setAliasLoading(true);
    try {
      const response = await authFetch(`${API_BASE}/admin/indexing/aliases?environment=${selectedEnvironment}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAliases(data.aliases || []);
      }
    } catch (err) {
      console.error('Error fetching aliases:', err);
    } finally {
      setAliasLoading(false);
    }
  }, [selectedEnvironment]);

  useEffect(() => {
    if (kbMode === 'sources') fetchAliases();
  }, [kbMode, fetchAliases]);

  const handlePromote = async (indexId: string, aliasName: string) => {
    setAliasAction(`promoting:${indexId}`);
    try {
      const response = await authFetch(`${API_BASE}/admin/indexing/aliases/${aliasName}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ indexName: indexId, environment: selectedEnvironment }),
      });
      const data = await response.json();
      if (data.success) {
        fetchAliases();
        onRefreshIndexes?.();
      } else {
        alert(`Failed to promote: ${data.error}`);
      }
    } catch (err: any) {
      alert(`Failed to promote: ${err.message}`);
    } finally {
      setAliasAction(null);
    }
  };

  const handleRollback = async (aliasName: string) => {
    if (!window.confirm(`Rollback alias "${aliasName}" to its previous index?`)) return;
    setAliasAction(`rolling-back:${aliasName}`);
    try {
      const response = await authFetch(`${API_BASE}/admin/indexing/aliases/${aliasName}/rollback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ environment: selectedEnvironment }),
      });
      const data = await response.json();
      if (data.success) {
        alert(`Rolled back "${aliasName}" to "${data.rolledBackTo}"`);
        fetchAliases();
        onRefreshIndexes?.();
      } else {
        alert(`Rollback failed: ${data.error}`);
      }
    } catch (err: any) {
      alert(`Rollback failed: ${err.message}`);
    } finally {
      setAliasAction(null);
    }
  };

  const handleCreateAlias = async () => {
    if (!newAliasName || !newAliasTarget) return;
    setAliasAction('creating');
    try {
      const response = await authFetch(`${API_BASE}/admin/indexing/aliases`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: newAliasName, indexName: newAliasTarget, environment: selectedEnvironment }),
      });
      const data = await response.json();
      if (data.success) {
        setShowAliasPanel(false);
        setNewAliasName('');
        setNewAliasTarget('');
        fetchAliases();
      } else {
        alert(`Failed to create alias: ${data.error}`);
      }
    } catch (err: any) {
      alert(`Failed to create alias: ${err.message}`);
    } finally {
      setAliasAction(null);
    }
  };

  // ---- Reconcile: sync dashboard state with Azure Search ----
  const handleReconcile = async () => {
    setReconcileLoading(true);
    setReconcileResult(null);
    try {
      const response = await authFetch(`${API_BASE}/admin/indexing/reconcile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ environment: selectedEnvironment }),
      });
      const data = await response.json();
      if (data.success) {
        setReconcileResult(data);
        fetchAliases();
        onRefreshIndexes?.();
      } else {
        alert(`Reconcile failed: ${data.error}`);
      }
    } catch (err: any) {
      alert(`Reconcile failed: ${err.message}`);
    } finally {
      setReconcileLoading(false);
    }
  };

  // ---- Open inline test panel for a specific index ----
  const handleTestIndex = async (agentId: string, indexName: string, agentDisplayName: string) => {
    setTestingBatch({
      batchId: `inline-test-${agentId}`,
      agentId,
      agentDisplayName,
      environment: selectedEnvironment,
      status: 'testing' as any,
      indexName,
      createdAt: new Date().toISOString(),
      createdBy: 'admin',
      changes: { added: [], modified: [], removed: [] },
      scope: 'full',
    });
  };

  // ---- Sample data for expanded rows ----
  const fetchSampleData = async (indexId: string) => {
    if (sampleData[indexId]) return; // already loaded
    setLoadingSample(indexId);
    try {
      const response = await authFetch(
        `${API_BASE}/admin/indexing/indexes/${indexId}/sample?environment=${selectedEnvironment}&count=2`
      );
      if (response.ok) {
        const data = await response.json();
        setSampleData(prev => ({ ...prev, [indexId]: data }));
      }
    } catch (err) {
      console.error('Error fetching sample:', err);
    } finally {
      setLoadingSample(null);
    }
  };

  const toggleAgentExpand = (agentId: string, indexId: string | null) => {
    if (expandedAgent === agentId) {
      setExpandedAgent(null);
    } else {
      setExpandedAgent(agentId);
      if (indexId) fetchSampleData(indexId);
    }
  };

  // ---- Document Changes / Activity ----
  const fetchActivity = useCallback(async () => {
    setActivityLoading(true);
    setActivityError(null);
    try {
      const response = await authFetch(
        `${API_BASE}/graph/sharepoint/activity?site=${siteFilter}&limit=50`
      );
      const data = await response.json();
      if (data.success) {
        setActivities(data.activities || []);
      } else {
        setActivityError(data.error || 'Failed to load activity');
      }
    } catch (err: any) {
      setActivityError(err.message || 'Failed to connect');
    } finally {
      setActivityLoading(false);
    }
  }, [siteFilter]);

  useEffect(() => {
    if (kbMode === 'activity') fetchActivity();
  }, [kbMode, fetchActivity]);

  const formatFileSize = (bytes: number): string => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // ---- AskAldoKB SharePoint browsing ----
  const fetchKBItems = useCallback(async (path: string) => {
    setKbLoading(true);
    setKbError(null);
    try {
      const response = await authFetch(
        `${API_BASE}/graph/sharepoint/askaldokb/browse?folderPath=${encodeURIComponent(path)}`
      );
      const data = await response.json();
      if (response.status === 403) {
        // User is not a member/owner of the AskAldoKB SharePoint site
        setKbError(data.error || 'Access denied -- you must be a member or owner of the AskAldoKB SharePoint site to browse documents.');
        setKbItems([]);
      } else if (data.success) {
        setKbItems(data.items || []);
      } else {
        setKbError(data.error || 'Failed to load');
        setKbItems([]);
      }
    } catch (err: any) {
      setKbError(err.message || 'Failed to connect');
      setKbItems([]);
    } finally {
      setKbLoading(false);
    }
  }, []);

  const fetchKBSiteInfo = useCallback(async () => {
    try {
      const response = await authFetch(`${API_BASE}/graph/sharepoint/askaldokb/site`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) setKbSiteInfo(data);
    } catch (err) {
      console.error('Failed to fetch AskAldoKB site info:', err);
    }
  }, []);

  useEffect(() => {
    if (kbMode === 'browse') {
      fetchKBItems(kbPath);
      if (!kbSiteInfo) fetchKBSiteInfo();
    }
  }, [kbMode, kbPath]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Copy utility: dry run ----
  const runDryRun = async (containerName: string, targetFolder: string) => {
    setCopyRunning(true);
    setCopyDryRun(null);
    setCopyResult(null);
    try {
      const response = await authFetch(`${API_BASE}/graph/sharepoint/askaldokb/copy-from-blob`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ containerName, targetFolder, dryRun: true })
      });
      const data = await response.json();
      setCopyDryRun(data);
    } catch (err: any) {
      setCopyDryRun({ success: false, error: err.message });
    } finally {
      setCopyRunning(false);
    }
  };

  const runCopy = async (containerName: string, targetFolder: string) => {
    setCopyRunning(true);
    setCopyResult(null);
    try {
      const response = await authFetch(`${API_BASE}/graph/sharepoint/askaldokb/copy-from-blob`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ containerName, targetFolder, dryRun: false })
      });
      const data = await response.json();
      setCopyResult(data);
    } catch (err: any) {
      setCopyResult({ success: false, error: err.message });
    } finally {
      setCopyRunning(false);
    }
  };

  const getFileIcon = (filename: string, isFolder: boolean) => {
    if (isFolder) return <Folder size={20} />;
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return <FileText size={20} style={{ color: '#ef4444' }} />;
      case 'doc':
      case 'docx': return <FileText size={20} style={{ color: 'var(--idx-text-link, #3b82f6)' }} />;
      case 'xls':
      case 'xlsx': return <FileSpreadsheet size={20} style={{ color: '#22c55e' }} />;
      case 'ppt':
      case 'pptx': return <FileType size={20} style={{ color: '#f97316' }} />;
      case 'html':
      case 'htm': return <Globe size={20} style={{ color: '#10b981' }} />;
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'bmp': return <Image size={20} style={{ color: '#06b6d4' }} />;
      case 'md': return <FileText size={20} style={{ color: '#6f747b' }} />;
      default: return <FileText size={20} />;
    }
  };

  const formatSize = (bytes: number): string => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  return (
    <div className="sharepoint-tab">
      <div className="idx-tab-header">
        <h2>Knowledge Base</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => setKbMode('browse')}
            className="idx-mode-btn"
            style={{
              background: kbMode === 'browse' ? '#10b981' : 'var(--idx-bg-primary)',
              color: kbMode === 'browse' ? '#fff' : 'var(--idx-text-secondary)',
              borderColor: kbMode === 'browse' ? '#10b981' : 'var(--idx-border-strong)',
            }}
          >
            <FolderOpen size={14} /> Browse Documents
          </button>
          <button
            onClick={() => setKbMode('sources')}
            className="idx-mode-btn"
            style={{
              background: kbMode === 'sources' ? '#10b981' : 'var(--idx-bg-primary)',
              color: kbMode === 'sources' ? '#fff' : 'var(--idx-text-secondary)',
              borderColor: kbMode === 'sources' ? '#10b981' : 'var(--idx-border-strong)',
            }}
          >
            <Database size={14} /> Index Sources
          </button>
          <button
            onClick={() => setKbMode('changes')}
            className="idx-mode-btn"
            style={{
              background: kbMode === 'changes' ? '#f59e0b' : 'var(--idx-bg-primary)',
              color: kbMode === 'changes' ? '#fff' : 'var(--idx-text-secondary)',
              borderColor: kbMode === 'changes' ? '#f59e0b' : 'var(--idx-border-strong)',
            }}
          >
            <AlertTriangle size={14} /> Change Detection
          </button>
          <button
            onClick={() => setKbMode('activity')}
            className="idx-mode-btn"
            style={{
              background: kbMode === 'activity' ? '#10b981' : 'var(--idx-bg-primary)',
              color: kbMode === 'activity' ? '#fff' : 'var(--idx-text-secondary)',
              borderColor: kbMode === 'activity' ? '#10b981' : 'var(--idx-border-strong)',
            }}
          >
            <Activity size={14} /> Activity Log
          </button>
          <button
            onClick={() => setKbMode('copy')}
            className="idx-mode-btn"
            style={{
              background: kbMode === 'copy' ? '#10b981' : 'var(--idx-bg-primary)',
              color: kbMode === 'copy' ? '#fff' : 'var(--idx-text-secondary)',
              borderColor: kbMode === 'copy' ? '#10b981' : 'var(--idx-border-strong)',
            }}
          >
            <Copy size={14} /> Blob to SP Copy
          </button>
        </div>
      </div>

      {/* ================================================================= */}
      {/* Browse Documents Mode                                              */}
      {/* ================================================================= */}
      {kbMode === 'browse' && (
        <>
          {/* AskAldoKB Browser */}
          {kbSiteInfo && (
            <div style={{ padding: '8px 16px', background: 'var(--idx-bg-inset)', borderRadius: 8, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--idx-text-secondary)' }}>
              <Globe size={14} style={{ color: 'var(--idx-text-link)' }} />
              <span style={{ fontWeight: 500 }}>{kbSiteInfo.site?.displayName}</span>
              <span style={{ color: 'var(--idx-text-muted)' }}>/</span>
              <span style={{ color: 'var(--idx-text-muted)' }}>{kbSiteInfo.drive?.name}</span>
              <a
                href="https://unifirst.sharepoint.com/sites/AskAldoKB/Shared%20Documents/Forms/AllItems.aspx"
                target="_blank"
                rel="noopener noreferrer"
                style={{ marginLeft: 'auto', color: 'var(--idx-text-link)', display: 'flex', alignItems: 'center', gap: 4, textDecoration: 'none', fontSize: 12 }}
              >
                <ExternalLink size={12} /> Open in SharePoint
              </a>
            </div>
          )}

          <div className="idx-browser-controls">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
              <FolderOpen size={16} style={{ color: 'var(--idx-text-muted)' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                <span
                  style={{ cursor: 'pointer', color: 'var(--idx-text-link)', fontWeight: 500 }}
                  onClick={() => setKbPath('')}
                >
                  Root
                </span>
                {kbPath.split('/').filter(Boolean).map((part, i, arr) => {
                  const pathTo = arr.slice(0, i + 1).join('/');
                  return (
                    <React.Fragment key={i}>
                      <ChevronRight size={12} style={{ color: 'var(--idx-text-faint)' }} />
                      <span
                        style={{ cursor: 'pointer', color: i === arr.length - 1 ? 'var(--idx-text-primary)' : 'var(--idx-text-link)', fontWeight: i === arr.length - 1 ? 600 : 400 }}
                        onClick={() => setKbPath(pathTo)}
                      >
                        {part}
                      </span>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {kbPath && (
              <button
                className="idx-back-btn"
                onClick={() => setKbPath(kbPath.split('/').slice(0, -1).join('/'))}
              >
                <ArrowLeft size={16} /> Back
              </button>
            )}

            <button
              className="idx-back-btn"
              onClick={() => fetchKBItems(kbPath)}
              disabled={kbLoading}
            >
              <RefreshCw size={14} className={kbLoading ? 'spinning' : ''} /> Refresh
            </button>
          </div>

          {kbError && (
            <div style={{
              padding: 12,
              background: kbError.includes('Access denied') ? '#fef3c7' : '#fef2f2',
              border: `1px solid ${kbError.includes('Access denied') ? '#fde68a' : '#fecaca'}`,
              borderRadius: 8,
              color: kbError.includes('Access denied') ? '#92400e' : '#991b1b',
              fontSize: 13,
              marginBottom: 8,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8
            }}>
              {kbError.includes('Access denied')
                ? <ShieldAlert size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                : <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              }
              <div>
                {kbError}
                {kbError.includes('Access denied') && (
                  <div style={{ fontSize: 11, marginTop: 4, opacity: 0.85 }}>
                    Document access is restricted to members and owners of the AskAldoKB SharePoint site. Contact the site owner to request access.
                  </div>
                )}
              </div>
            </div>
          )}

          {kbLoading ? (
            <div className="idx-loading">
              <div className="idx-spinner" />
              <p>Loading from AskAldoKB...</p>
            </div>
          ) : kbItems.length === 0 && !kbError ? (
            <div className="idx-empty-state">
              <p>No items in this folder</p>
            </div>
          ) : (
            <div className="idx-documents-list">
              {kbItems.map((item, index) => (
                <div
                  key={item.id || index}
                  className={`idx-document-item ${item.isFolder ? 'folder' : 'file'}`}
                  onClick={() => item.isFolder && setKbPath(item.path)}
                  style={{ cursor: item.isFolder ? 'pointer' : 'default' }}
                >
                  <span className={`icon ${item.isFolder ? 'folder' : ''}`}>
                    {getFileIcon(item.name, item.isFolder)}
                  </span>
                  <span className="name">
                    {item.name}
                    {item.isFolder && item.childCount > 0 && (
                      <span style={{ color: 'var(--idx-text-faint)', fontSize: 11, marginLeft: 6 }}>({item.childCount} items)</span>
                    )}
                  </span>
                  <span className="size">{item.isFolder ? '' : formatSize(item.size)}</span>
                  <span className="modified">
                    {item.modified ? new Date(item.modified).toLocaleDateString() : ''}
                  </span>
                  {item.webUrl && !item.isFolder && (
                    <a
                      href={item.webUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: 'var(--idx-text-link, #3b82f6)', padding: '2px 6px' }}
                      title="Open in SharePoint"
                    >
                      <ExternalLink size={14} />
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ================================================================= */}
      {/* Index Sources Mode                                                 */}
      {/* ================================================================= */}
      {kbMode === 'sources' && (
        <>
          {/* Header with alias + refresh controls */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--idx-text-muted)' }}>
              {indexes.length} live indexes in <strong>{selectedEnvironment.toUpperCase()}</strong>
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={handleReconcile}
                disabled={reconcileLoading}
                style={{
                  padding: '4px 10px', borderRadius: 6, border: '1px solid #059669', cursor: reconcileLoading ? 'not-allowed' : 'pointer',
                  background: '#ecfdf5', color: '#059669',
                  fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4,
                }}
                title="Read actual Azure Search state and sync changelogs + approval batches"
              >
                {reconcileLoading ? <Loader2 size={12} className="animate-spin" /> : <Database size={12} />}
                {reconcileLoading ? 'Syncing...' : 'Sync State from Azure'}
              </button>
              <button
                onClick={() => setShowAliasPanel(!showAliasPanel)}
                style={{
                  padding: '4px 10px', borderRadius: 6, border: '1px solid #6ee7b7', cursor: 'pointer',
                  background: showAliasPanel ? '#059669' : '#ecfdf5',
                  color: showAliasPanel ? '#fff' : '#047857',
                  fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4,
                }}
              >
                <GitBranch size={12} /> Aliases ({aliases.length})
              </button>
              <button
                onClick={() => { onRefreshIndexes?.(); fetchAliases(); }}
                style={{
                  padding: '4px 10px', borderRadius: 6, border: '1px solid var(--idx-border-strong)', cursor: 'pointer',
                  background: 'var(--idx-bg-primary)', color: 'var(--idx-text-secondary)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4,
                }}
              >
                <RefreshCw size={12} /> Refresh
              </button>
            </div>
          </div>

          {/* Alias management panel (collapsible) */}
          {showAliasPanel && (
            <div style={{ margin: '0 0 14px', padding: 14, background: 'var(--idx-bg-success)', border: '1px solid #6ee7b7', borderRadius: 10 }}>
              <div style={{ fontSize: 12, color: 'var(--idx-text-muted)', marginBottom: 10 }}>
                Aliases let you swap which index is live without changing agent config. Agents query the alias; you swap the underlying index.
              </div>
              {aliases.length > 0 && (
                <div style={{ display: 'grid', gap: 6, marginBottom: 10 }}>
                  {aliases.map(alias => (
                    <div key={alias.name} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                      background: 'var(--idx-bg-primary)', border: '1px solid var(--idx-border)', borderRadius: 8, fontSize: 12,
                    }}>
                      <GitBranch size={13} style={{ color: '#10b981', flexShrink: 0 }} />
                      <span style={{ fontWeight: 600, minWidth: 120, color: 'var(--idx-text-primary)' }}>{alias.name}</span>
                      <ArrowLeftRight size={11} style={{ color: 'var(--idx-text-faint)' }} />
                      <span style={{ fontFamily: 'monospace', color: 'var(--idx-text-mono)' }}>{alias.indexes?.[0] || 'none'}</span>
                      {alias.history?.previousIndex && (
                        <span style={{ fontSize: 11, color: 'var(--idx-text-muted)' }}>(prev: {alias.history.previousIndex})</span>
                      )}
                      {alias.history?.previousIndex && (
                        <button
                          onClick={() => handleRollback(alias.name)}
                          disabled={!!aliasAction}
                          style={{
                            marginLeft: 'auto', padding: '3px 8px', fontSize: 11, borderRadius: 5,
                            border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3, fontWeight: 500,
                          }}
                        >
                          {aliasAction === `rolling-back:${alias.name}` ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
                          Rollback
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="text" placeholder="Alias name (e.g. hrpolicy-live)"
                  value={newAliasName} onChange={e => setNewAliasName(e.target.value)}
                  style={{ padding: '5px 8px', borderRadius: 5, border: '1px solid var(--idx-border-strong)', fontSize: 12, width: 180 }}
                />
                <select
                  value={newAliasTarget} onChange={e => setNewAliasTarget(e.target.value)}
                  style={{ padding: '5px 8px', borderRadius: 5, border: '1px solid var(--idx-border-strong)', fontSize: 12, minWidth: 200 }}
                >
                  <option value="">Target index...</option>
                  {indexes.map(idx => <option key={idx.id} value={idx.id}>{idx.id}</option>)}
            </select>
                <button
                  onClick={handleCreateAlias}
                  disabled={!newAliasName || !newAliasTarget || !!aliasAction}
                  style={{
                    padding: '5px 12px', borderRadius: 5, fontSize: 12, fontWeight: 500,
                    background: (!newAliasName || !newAliasTarget) ? '#e3e5e8' : '#059669',
                    color: (!newAliasName || !newAliasTarget) ? '#9da2a8' : '#fff',
                    border: 'none', cursor: (!newAliasName || !newAliasTarget) ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: 3,
                  }}
                >
                  {aliasAction === 'creating' ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
                  Create
              </button>
              </div>
            </div>
          )}

          {/* Reconcile result banner */}
          {reconcileResult && (
            <div style={{
              padding: '10px 14px', marginBottom: 12, borderRadius: 8,
              background: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <CheckCircle size={14} style={{ color: '#059669' }} />
                <strong style={{ color: '#065f46' }}>
                  State synced from Azure Search ({reconcileResult.environment?.toUpperCase()})
                </strong>
                <button onClick={() => setReconcileResult(null)} style={{ marginLeft: 'auto', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--idx-text-muted)' }}>
                  <XCircle size={14} />
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 6 }}>
                {reconcileResult.agents?.map((a: any) => (
                  <div key={a.agentId} style={{
                    padding: '6px 10px', background: 'var(--idx-bg-primary)', borderRadius: 6, border: '1px solid var(--idx-border)',
                    display: 'flex', alignItems: 'center', gap: 6, fontSize: 11,
                  }}>
                    <span style={{ fontWeight: 600 }}>{a.displayName}</span>
                    <span style={{ fontFamily: 'monospace', color: a.aliasExists ? '#059669' : '#d97706' }}>
                      {a.aliasExists ? `alias: ${a.aliasName}` : 'no alias'}
                    </span>
                    <span style={{ marginLeft: 'auto', color: 'var(--idx-text-muted)' }}>
                      {a.indexes?.length || 0} index{(a.indexes?.length || 0) !== 1 ? 'es' : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Unified agent → index table with expandable rows */}
          {registryAgents.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--idx-border)', textAlign: 'left' }}>
                    <th style={{ padding: '8px 10px', color: 'var(--idx-text-secondary)', fontWeight: 600, width: 28 }}></th>
                    <th style={{ padding: '8px 10px', color: 'var(--idx-text-secondary)', fontWeight: 600 }}>Agent</th>
                    <th style={{ padding: '8px 10px', color: 'var(--idx-text-secondary)', fontWeight: 600 }}>Type</th>
                    <th style={{ padding: '8px 10px', color: 'var(--idx-text-secondary)', fontWeight: 600 }}>Index ({selectedEnvironment.toUpperCase()})</th>
                    <th style={{ padding: '8px 10px', color: 'var(--idx-text-secondary)', fontWeight: 600 }}>Docs</th>
                    <th style={{ padding: '8px 10px', color: 'var(--idx-text-secondary)', fontWeight: 600 }}>Source</th>
                    <th style={{ padding: '8px 10px', color: 'var(--idx-text-secondary)', fontWeight: 600 }}>Sync</th>
                    <th style={{ padding: '8px 10px', color: 'var(--idx-text-secondary)', fontWeight: 600, width: 50 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {registryAgents
                    .filter(a => !a.hidden)
                    .sort((a, b) => {
                      // Agents with indexes first, then by status
                      const aIdx = getAgentIndex(a);
                      const bIdx = getAgentIndex(b);
                      if (aIdx && !bIdx) return -1;
                      if (!aIdx && bIdx) return 1;
                      const order: Record<string, number> = { active: 0, uat: 1, in_development: 2, planned: 3, deprecated: 4 };
                      return (order[a.status || 'planned'] ?? 5) - (order[b.status || 'planned'] ?? 5);
                    })
                    .map(agent => {
                      const kb = agent.knowledgeBase;
                      const resolvedName = resolveIndexName(agent);
                      const liveIndex = getAgentIndex(agent);
                      const isExpanded = expandedAgent === agent.id;
                      const docCount = liveIndex?.stats?.documentCount || liveIndex?.azure_search?.documentCount || liveIndex?.pinecone?.documentCount || 0;
                      const hasAlias = liveIndex ? (indexToAliases[liveIndex.id]?.length > 0) : false;
                      const typeColors: Record<string, string> = {
                        askaldokb: '#059669', external_sharepoint: '#d97706',
                        standalone_document: '#10b981', live_api: '#0ea5e9',
                      };
                      const typeLabels: Record<string, string> = {
                        askaldokb: 'AskAldoKB', external_sharepoint: 'Ext SP',
                        standalone_document: 'Standalone', live_api: 'Live API',
                      };

                      return (
                        <React.Fragment key={agent.id}>
                          {/* Main row */}
                          <tr
                            style={{
                              borderBottom: isExpanded ? 'none' : '1px solid var(--idx-border)',
                              cursor: resolvedName ? 'pointer' : 'default',
                              background: isExpanded ? 'var(--idx-bg-secondary)' : undefined,
                            }}
                            onClick={() => resolvedName && toggleAgentExpand(agent.id, liveIndex?.id || null)}
                          >
                            <td style={{ padding: '6px 4px 6px 10px', textAlign: 'center' }}>
                              {resolvedName ? (
                                <ChevronDown size={14} style={{
                                  color: 'var(--idx-text-faint)',
                                  transform: isExpanded ? 'rotate(180deg)' : 'none',
                                  transition: 'transform 0.15s',
                                }} />
                              ) : null}
                            </td>
                            <td style={{ padding: '6px 10px', fontWeight: 500, color: 'var(--idx-text-primary)' }}>
                              {agent.name}
                              <span style={{ color: 'var(--idx-text-faint)', fontSize: '0.72rem', marginLeft: 6 }}>{agent.id}</span>
                            </td>
                            <td style={{ padding: '6px 10px' }}>
                              {kb ? (
                                <span style={{
                                  fontSize: '0.72rem', padding: '2px 8px', borderRadius: 10,
                                  color: typeColors[kb.type] || '#6f747b',
                                  backgroundColor: `${typeColors[kb.type] || '#6f747b'}12`,
                                  fontWeight: 500,
                                }}>
                                  {typeLabels[kb.type] || kb.type}
                                </span>
                              ) : (
                                <span style={{ color: '#cfd2d6', fontSize: '0.78rem' }}>—</span>
                              )}
                            </td>
                            <td style={{ padding: '6px 10px' }}>
                              {resolvedName ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: liveIndex ? 'var(--idx-text-mono)' : 'var(--idx-text-faint)' }}>
                                    {resolvedName}
                                  </span>
                                  {liveIndex ? (
                                    <CheckCircle size={12} style={{ color: '#059669', flexShrink: 0 }} />
                                  ) : (
                                    <span title="Not found in this environment"><XCircle size={12} style={{ color: '#cfd2d6', flexShrink: 0 }} /></span>
                                  )}
                                  {hasAlias && (
                                    <span style={{
                                      display: 'inline-flex', alignItems: 'center', gap: 2,
                                      padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 600,
                                      background: '#ecfdf5', color: '#047857', border: '1px solid #6ee7b7',
                                    }}>
                                      <GitBranch size={9} /> alias
                                    </span>
            )}
          </div>
                              ) : (
                                <span style={{ color: '#cfd2d6', fontSize: '0.78rem' }}>—</span>
                              )}
                            </td>
                            <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: '0.78rem', color: docCount > 0 ? 'var(--idx-text-mono)' : 'var(--idx-text-faint)' }}>
                              {docCount > 0 ? docCount.toLocaleString() : '—'}
                            </td>
                            <td style={{ padding: '6px 10px', fontSize: '0.78rem', color: 'var(--idx-text-muted)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {kb?.contentSources?.[0]?.name || '—'}
                            </td>
                            <td style={{ padding: '6px 10px' }}>
                              {kb?.sync ? (
                                kb.sync.automated ? (
                                  <span style={{
                                    fontSize: '0.72rem', padding: '2px 8px', borderRadius: 10,
                                    color: '#059669', backgroundColor: '#ecfdf5', fontWeight: 500,
                                  }}>
                                    <Zap size={10} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 2 }} />
                                    Auto
                                  </span>
                                ) : (
                                  <span style={{
                                    fontSize: '0.72rem', padding: '2px 8px', borderRadius: 10,
                                    color: '#d97706', backgroundColor: '#fffbeb', fontWeight: 500,
                                  }}>
                                    Manual
                                  </span>
                                )
                              ) : (
                                <span style={{ color: '#cfd2d6', fontSize: '0.78rem' }}>—</span>
                              )}
                            </td>
                            <td style={{ padding: '6px 10px' }}>
                              {liveIndex && onSync && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                                  <select
                                    value={captionFor(liveIndex)}
                                    onChange={e => setChoiceFor(liveIndex.id, e.target.value as ImageModeChoice)}
                                    disabled={syncingIndex === liveIndex.id}
                                    title="Image handling strategy for this (re)index. Vision-heavy strategies run Vision API calls (model configured via AZURE_OPENAI_VISION_DEPLOYMENT) and add cost/time. 'Default' uses the agent's configured setting. See docs/architecture/IMAGE_MODE_REFERENCE.md."
                                    style={{
                                      padding: '3px 6px', borderRadius: 5, fontSize: 11,
                                      border: '1px solid var(--idx-border-strong)',
                                      background: 'var(--idx-bg-primary)', color: 'var(--idx-text-secondary)',
                                      cursor: syncingIndex === liveIndex.id ? 'not-allowed' : 'pointer',
                                    }}
                                  >
                                    {getResolvedImageModeOptions().map(opt => (
                                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                  </select>
                                  {isVisionHeavy(captionFor(liveIndex)) && (
                                    <input
                                      type="number"
                                      min={0}
                                      step={1}
                                      value={maxVisionCallsFor(liveIndex)}
                                      onChange={e => setMaxVisionCallsFor(liveIndex.id, e.target.value)}
                                      disabled={syncingIndex === liveIndex.id}
                                      placeholder="cap"
                                      title="Max Vision API calls for this run (blank = unlimited / agent default). Caps protect against a runaway bill on a large or unfamiliar document set — capped images fall back to a placeholder instead of failing the ingest."
                                      style={{
                                        width: 56, padding: '3px 6px', borderRadius: 5, fontSize: 11,
                                        border: '1px solid var(--idx-border-strong)',
                                        background: 'var(--idx-bg-primary)', color: 'var(--idx-text-secondary)',
                                      }}
                                    />
                                  )}
                                  <button
                                    onClick={() => {
                                      const choice = captionFor(liveIndex);
                                      const cap = parseVisionCap(maxVisionCallsFor(liveIndex));
                                      if (isVisionHeavy(choice) && !window.confirm(
                                        `Sync ${liveIndex.id} with image strategy "${choice}"?\n\n` +
                                        `This runs Vision API calls per image (cached images are reused) and ` +
                                        `increases cost and index time.${cap !== undefined ? ` Capped at ${cap} real Vision calls this run.` : ' No cap set — unlimited calls.'} Continue?`
                                      )) return;
                                      onSync(liveIndex.id, 'incremental', captionToImageMode(choice), cap);
                                    }}
                                    disabled={syncingIndex === liveIndex.id}
                                    style={{
                                      padding: '3px 8px', borderRadius: 5, fontSize: 11, border: '1px solid var(--idx-border-strong)',
                                      background: 'var(--idx-bg-primary)', color: 'var(--idx-text-secondary)',
                                      cursor: syncingIndex === liveIndex.id ? 'not-allowed' : 'pointer',
                                      display: 'flex', alignItems: 'center', gap: 3,
                                    }}
                                    title="Trigger incremental reindex (only changed pages)"
                                  >
                                    {syncingIndex === liveIndex.id
                                      ? <Loader2 size={11} className="animate-spin" />
                                      : <RefreshCw size={11} />}
                                    Sync
                                  </button>
                                  <button
                                    onClick={() => {
                                      const choice = captionFor(liveIndex);
                                      const cap = parseVisionCap(maxVisionCallsFor(liveIndex));
                                      const captionNote = isVisionHeavy(choice)
                                        ? `\n\n⚠️ Image strategy "${choice}" runs Vision API calls per image, adding cost and index time.` +
                                          (cap !== undefined ? ` Capped at ${cap} real Vision calls this run.` : ' No cap set — unlimited calls.')
                                        : '';
                                      if (window.confirm(`Full rebuild of ${liveIndex.id}? This re-processes ALL pages and may take a few minutes.${captionNote}`)) {
                                        onSync(liveIndex.id, 'full', captionToImageMode(choice), cap);
                                      }
                                    }}
                                    disabled={syncingIndex === liveIndex.id}
                                    style={{
                                      padding: '3px 8px', borderRadius: 5, fontSize: 11, border: '1px solid #fbbf24',
                                      background: '#fffbeb', color: '#92400e',
                                      cursor: syncingIndex === liveIndex.id ? 'not-allowed' : 'pointer',
                                      display: 'flex', alignItems: 'center', gap: 3,
                                    }}
                                    title="Full rebuild — re-processes all pages from scratch"
                                  >
                                    Full
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>

                          {/* Expanded detail row */}
                          {isExpanded && (
                            <tr>
                              <td colSpan={8} style={{ padding: 0, background: 'var(--idx-bg-secondary)', borderBottom: '1px solid var(--idx-border)' }}>
                                <div style={{ padding: '12px 20px 14px 44px', display: 'flex', flexDirection: 'column', gap: 10 }}>

                                  {/* Stats row */}
                                  {liveIndex && (
                                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '0.8rem' }}>
                                      <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--idx-text-secondary)' }}>
                                        <FileText size={13} /> {docCount.toLocaleString()} docs
                                      </span>
                                      {liveIndex.azure_search?.fields && (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--idx-text-secondary)' }}>
                                          <Settings size={13} /> {liveIndex.azure_search.fields} fields
                                        </span>
                                      )}
                                      {liveIndex.pinecone?.dimension && (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--idx-text-secondary)' }}>
                                          <Cloud size={13} /> {liveIndex.pinecone.dimension}d vectors
                                        </span>
                                      )}
                                      {liveIndex.lastSync && (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--idx-text-muted)' }}>
                                          <Clock size={13} /> Last sync: {formatRelativeTime(liveIndex.lastSync.timestamp)}
                                          <span style={{
                                            padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 500,
                                            background: liveIndex.lastSync.status === 'completed' ? '#ecfdf5' : '#fef2f2',
                                            color: liveIndex.lastSync.status === 'completed' ? '#059669' : '#dc2626',
                                          }}>
                                            {liveIndex.lastSync.status}
                                          </span>
                                        </span>
                                      )}
                                      {liveIndex.changeTracking && liveIndex.changeTracking.totalChanges > 0 && (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                          {liveIndex.changeTracking.documentsAdded > 0 && <span style={{ color: '#059669', fontWeight: 500 }}>+{liveIndex.changeTracking.documentsAdded}</span>}
                                          {liveIndex.changeTracking.documentsUpdated > 0 && <span style={{ color: '#d97706', fontWeight: 500 }}>~{liveIndex.changeTracking.documentsUpdated}</span>}
                                          {liveIndex.changeTracking.documentsDeleted > 0 && <span style={{ color: '#dc2626', fontWeight: 500 }}>-{liveIndex.changeTracking.documentsDeleted}</span>}
                                        </span>
                                      )}
            </div>
          )}

                                  {!liveIndex && resolvedName && (
                                    <div style={{ fontSize: '0.82rem', color: 'var(--idx-text-faint)', fontStyle: 'italic' }}>
                                      Index <code>{resolvedName}</code> not found in {selectedEnvironment.toUpperCase()}. It may not be created yet.
                </div>
                                  )}

                                  {/* Alias info for this index */}
                                  {liveIndex && indexToAliases[liveIndex.id]?.length > 0 && (
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                                      <span style={{ fontSize: '0.78rem', color: 'var(--idx-text-muted)' }}>Aliases:</span>
                                      {indexToAliases[liveIndex.id].map((alias: any) => (
                                        <span key={alias.name} style={{
                                          display: 'inline-flex', alignItems: 'center', gap: 3,
                                          padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                                          background: '#ecfdf5', color: '#047857', border: '1px solid #6ee7b7',
                                        }}>
                                          <GitBranch size={10} /> {alias.name}
                                        </span>
                                      ))}
                                    </div>
                                  )}

                                  {/* Promote via alias — if aliases exist and this index is NOT already aliased */}
                                  {liveIndex && aliases.length > 0 && !indexToAliases[liveIndex.id]?.length && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <span style={{ fontSize: '0.78rem', color: 'var(--idx-text-muted)' }}>Promote via alias:</span>
                                      <select
                                        onChange={e => {
                                          if (e.target.value) handlePromote(liveIndex.id, e.target.value);
                                          e.target.value = '';
                                        }}
                                        disabled={!!aliasAction}
                                        style={{
                                          padding: '3px 6px', borderRadius: 5, fontSize: 11, border: '1px solid #6ee7b7',
                                          background: '#ecfdf5', color: '#047857', cursor: 'pointer', fontWeight: 500,
                                        }}
                                      >
                                        <option value="">Select alias...</option>
                                        {aliases.map(a => (
                                          <option key={a.name} value={a.name}>{a.name} (→ {a.indexes?.[0]})</option>
                                        ))}
                                      </select>
                                    </div>
                                  )}

                                  {/* Test Index — opens test panel for this agent */}
                                  {liveIndex && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleTestIndex(
                                            agent.id,
                                            liveIndex.id,
                                            agent.name
                                          );
                                        }}
                                        style={{
                                          padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500,
                                          background: '#ecfdf5', color: '#047857', border: '1px solid #6ee7b7',
                                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                                        }}
                                      >
                                        <MessageSquare size={12} /> Test Index
                                      </button>
                                      <span style={{ fontSize: 11, color: 'var(--idx-text-faint)' }}>
                                        Send test queries to <code style={{ fontSize: 10 }}>{liveIndex.id}</code>
                                      </span>
                                    </div>
                                  )}

                                  {/* Content sources detail */}
                                  {kb?.contentSources && kb.contentSources.length > 0 && (
                                    <div>
                                      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--idx-text-secondary)', marginBottom: 4 }}>Content Sources</div>
                                      {kb.contentSources.map((src, sIdx) => (
                                        <div key={sIdx} style={{
                                          display: 'flex', alignItems: 'center', gap: 8,
                                          padding: '4px 10px', background: 'var(--idx-bg-primary)', borderRadius: 6,
                                          border: src.isPrimary ? '1px solid #bbf7d0' : '1px solid var(--idx-border)',
                                          marginBottom: 4, fontSize: '0.78rem', color: 'var(--idx-text-secondary)',
                                        }}>
                                          {src.isPrimary && <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 6, background: '#ecfdf5', color: '#059669', fontWeight: 600 }}>PRIMARY</span>}
                                          <span style={{ fontWeight: 500 }}>{src.name}</span>
                                          <span style={{ color: 'var(--idx-text-faint)', fontFamily: 'monospace', fontSize: '0.72rem' }}>{src.location}</span>
                                          {src.url && (
                                            <a href={src.url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', marginLeft: 'auto' }}>
                                              <ExternalLink size={11} />
                                            </a>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Sync details */}
                                  {kb?.sync && (
                                    <div style={{
                                      padding: '8px 10px', borderRadius: 6, fontSize: '0.78rem',
                                      background: kb.sync.automated ? '#f0fdf4' : '#fefce8',
                                      border: `1px solid ${kb.sync.automated ? '#bbf7d0' : '#fef08a'}`,
                                    }}>
                                      <strong>{kb.sync.automated ? 'Automated' : 'Manual'}</strong>
                                      {kb.sync.schedule && <span style={{ marginLeft: 8, color: 'var(--idx-text-muted)' }}>{kb.sync.schedule}</span>}
                                      {kb.sync.pipeline && <span style={{ marginLeft: 8, fontFamily: 'monospace', color: 'var(--idx-text-muted)' }}>{kb.sync.pipeline}</span>}
                                      {kb.sync.notes && <div style={{ color: 'var(--idx-text-faint)', marginTop: 3, fontStyle: 'italic' }}>{kb.sync.notes}</div>}
                                    </div>
                                  )}

                                  {/* Schema / sample preview */}
                                  {liveIndex && sampleData[liveIndex.id] && (
                                    <div style={{ marginTop: 2 }}>
                                      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--idx-text-secondary)', marginBottom: 4 }}>
                                        Schema Preview
                                        {sampleData[liveIndex.id].schema && (
                                          <span style={{ fontWeight: 400, color: 'var(--idx-text-faint)', marginLeft: 8 }}>
                                            {sampleData[liveIndex.id].schema.fields.length} fields
                                            {sampleData[liveIndex.id].schema.vectorSearch && ' · Vector Search'}
                                          </span>
                                        )}
                                      </div>
                                      <div style={{ maxHeight: 240, overflow: 'auto', borderRadius: 6, border: '1px solid var(--idx-border)' }}>
                                        {sampleData[liveIndex.id].documents?.map((doc: any, i: number) => (
                                          <pre key={i} style={{
                                            margin: 0, padding: '8px 12px', fontSize: 11, fontFamily: 'monospace',
                                            background: i % 2 === 0 ? 'var(--idx-bg-primary)' : 'var(--idx-bg-secondary)',
                                            borderBottom: '1px solid var(--idx-border)', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                                            color: 'var(--idx-text-mono)',
                                          }}>
                                            {JSON.stringify(doc, null, 2)}
                                          </pre>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {liveIndex && loadingSample === liveIndex.id && (
                                    <div style={{ fontSize: '0.78rem', color: 'var(--idx-text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <Loader2 size={13} className="animate-spin" /> Loading schema...
                                    </div>
                                  )}

                                  {/* Per-environment index list */}
                                  {kb?.environments && Object.keys(kb.environments).length > 1 && (
                                    <div>
                                      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--idx-text-secondary)', marginBottom: 4 }}>All Environments</div>
                                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                        {Object.entries(kb.environments).map(([env, idx]) => (
                                          <span key={env} style={{
                                            fontSize: '0.72rem', padding: '2px 8px', borderRadius: 8,
                                            background: env === selectedEnvironment ? '#dbeafe' : 'var(--idx-bg-secondary)',
                                            border: env === selectedEnvironment ? '1px solid #93c5fd' : '1px solid var(--idx-border)',
                                            fontFamily: 'monospace', color: 'var(--idx-text-muted)',
                                          }}>
                                            <strong>{env.toUpperCase()}</strong>: {idx as string}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ================================================================= */}
      {/* Activity Log Mode — who changed what, when                        */}
      {/* ================================================================= */}
      {kbMode === 'activity' && (
        <>
          {/* Site filter */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            {(['askaldokb', 'askiris', 'all'] as const).map(site => (
              <button
                key={site}
                onClick={() => setSiteFilter(site)}
                style={{
                  padding: '4px 12px', borderRadius: 16, border: '1px solid var(--idx-border)', cursor: 'pointer',
                  background: siteFilter === site ? '#2b2e32' : 'var(--idx-bg-primary)',
                  color: siteFilter === site ? '#fff' : 'var(--idx-text-secondary)',
                  fontSize: 12, fontWeight: 500,
                }}
              >
                {site === 'askaldokb' ? 'AskAldoKB' : site === 'askiris' ? 'AskIRIS' : 'All Sites'}
              </button>
            ))}
            <button
              onClick={fetchActivity}
              disabled={activityLoading}
              style={{ marginLeft: 'auto', padding: '4px 10px', borderRadius: 6, border: '1px solid var(--idx-border-strong)', cursor: 'pointer', background: 'var(--idx-bg-primary)', color: 'var(--idx-text-secondary)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <RefreshCw size={12} className={activityLoading ? 'spinning' : ''} /> Refresh
            </button>
          </div>

          {activityError && (
            <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', fontSize: 13, marginBottom: 8 }}>
              <AlertTriangle size={14} style={{ display: 'inline', marginRight: 6 }} />
              {activityError}
            </div>
          )}

          {activityLoading ? (
            <div className="idx-loading"><div className="idx-spinner" /><p>Loading activity...</p></div>
          ) : activities.length === 0 ? (
            <div className="idx-empty-state"><p>No recent changes found</p></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Header */}
              <div style={{
                display: 'grid', gridTemplateColumns: '80px 1fr 200px 160px 160px 80px',
                padding: '8px 12px', background: 'var(--idx-bg-secondary)', borderRadius: '8px 8px 0 0',
                fontSize: 11, fontWeight: 600, color: 'var(--idx-text-muted)', textTransform: 'uppercase' as const, letterSpacing: 0.5,
              }}>
                <span>Action</span>
                <span>File</span>
                <span>Modified By</span>
                <span>Modified</span>
                <span>Source</span>
                <span>Size</span>
              </div>

              {/* Rows */}
              {activities.map((entry, i) => (
                <div
                  key={entry.id || i}
                  style={{
                    display: 'grid', gridTemplateColumns: '80px 1fr 200px 160px 160px 80px',
                    padding: '10px 12px', borderBottom: '1px solid var(--idx-border)', alignItems: 'center',
                    fontSize: 13, color: 'var(--idx-text-secondary)',
                    background: i % 2 === 0 ? 'var(--idx-bg-primary)' : 'var(--idx-bg-secondary)',
                  }}
                >
                  {/* Action badge */}
                  <span>
                    <span style={{
                      padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                      background: entry.action === 'created' ? '#dcfce7' : entry.action === 'deleted' ? '#fee2e2' : '#dbeafe',
                      color: entry.action === 'created' ? '#166534' : entry.action === 'deleted' ? '#991b1b' : '#1e40af',
                    }}>
                      {entry.action === 'created' ? '+' : entry.action === 'deleted' ? '-' : '~'} {entry.action}
                    </span>
                  </span>

                  {/* File name + path */}
                  <div style={{ overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {entry.isFolder ? <Folder size={14} style={{ color: '#f59e0b', flexShrink: 0 }} /> : <FileText size={14} style={{ color: 'var(--idx-text-muted)', flexShrink: 0 }} />}
                      <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {entry.fileName}
                      </span>
                      {entry.webUrl && (
                        <a href={entry.webUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--idx-text-link)', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--idx-text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {entry.folderPath}
                    </div>
                  </div>

                  {/* Modified by */}
                  <div style={{ overflow: 'hidden' }}>
                    <div style={{ fontWeight: 500, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {entry.modifiedBy.displayName}
                    </div>
                    {entry.modifiedBy.email && (
                      <div style={{ fontSize: 11, color: 'var(--idx-text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {entry.modifiedBy.email}
                      </div>
                    )}
                  </div>

                  {/* Modified date */}
                  <div>
                    <div style={{ fontSize: 12 }}>{formatRelativeTime(entry.modifiedAt)}</div>
                    <div style={{ fontSize: 11, color: 'var(--idx-text-faint)' }}>
                      {new Date(entry.modifiedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </div>
                  </div>

                  {/* Source */}
                  <span style={{
                    padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 500,
                    background: entry.source === 'AskAldoKB' ? '#eff6ff' : '#ecfdf5',
                    color: entry.source === 'AskAldoKB' ? '#1e40af' : '#047857',
                    display: 'inline-block', width: 'fit-content',
                  }}>
                    {entry.source}
                  </span>

                  {/* Size */}
                  <span style={{ fontSize: 12, color: 'var(--idx-text-muted)' }}>{formatFileSize(entry.size)}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ================================================================= */}
      {/* Blob-to-SharePoint Copy Mode                                       */}
      {/* ================================================================= */}
      {kbMode === 'copy' && (
        <div style={{ padding: 16 }}>
          <div style={{ marginBottom: 16, padding: 12, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13 }}>
            <AlertTriangle size={14} style={{ display: 'inline', marginRight: 6, color: '#d97706' }} />
            <strong>Requires Sites.ReadWrite.All</strong> on the SharePoint-Webhook-Prototype app registration.
            The dry-run preview works with read-only permissions; actual copy requires write access.
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 13, color: 'var(--idx-text-primary)' }}>Select Agent</label>
            <select
              style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--idx-border-strong)', fontSize: 13, background: 'var(--idx-bg-primary)', color: 'var(--idx-text-primary)' }}
              value={copyAgent}
              onChange={(e) => {
                setCopyAgent(e.target.value);
                setCopyContainer('');
                setCopyDryRun(null);
                setCopyResult(null);
              }}
            >
              <option value="">Choose an agent...</option>
              {Object.entries(AGENT_BLOB_MAPPING).map(([agentId, mapping]) => (
                <option key={agentId} value={agentId}>
                  {getAgentDisplayName(agentId)} --  {mapping.sharepointFolder}
                </option>
              ))}
            </select>
          </div>

          {copyAgent && AGENT_BLOB_MAPPING[copyAgent] && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 13, color: 'var(--idx-text-primary)' }}>Select Container</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {AGENT_BLOB_MAPPING[copyAgent].containers.map(c => (
                  <div
                    key={c.name}
                    onClick={() => { setCopyContainer(c.name); setCopyDryRun(null); setCopyResult(null); }}
                    style={{
                      padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                      border: copyContainer === c.name ? '2px solid #3b82f6' : '1px solid var(--idx-border)',
                      background: copyContainer === c.name ? '#eff6ff' : 'var(--idx-bg-primary)',
                      display: 'flex', alignItems: 'center', gap: 10
                    }}
                  >
                    {c.type === 'source' ? <HardDrive size={16} style={{ color: '#10b981' }} /> : <Image size={16} style={{ color: '#06b6d4' }} />}
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 13, color: 'var(--idx-text-primary)' }}>{c.label}</div>
                      <div style={{ fontSize: 11, color: 'var(--idx-text-muted)' }}>{c.name}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {copyAgent && copyContainer && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button
                onClick={() => runDryRun(copyContainer, AGENT_BLOB_MAPPING[copyAgent].sharepointFolder)}
                disabled={copyRunning}
                style={{
                  padding: '8px 16px', borderRadius: 6, border: '1px solid var(--idx-border-strong)', cursor: copyRunning ? 'not-allowed' : 'pointer',
                  background: 'var(--idx-bg-primary)', color: 'var(--idx-text-secondary)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500
                }}
              >
                <Search size={14} /> {copyRunning ? 'Scanning...' : 'Preview (Dry Run)'}
              </button>
              {copyDryRun?.success && (
                <button
                  onClick={() => runCopy(copyContainer, AGENT_BLOB_MAPPING[copyAgent].sharepointFolder)}
                  disabled={copyRunning}
                  style={{
                    padding: '8px 16px', borderRadius: 6, border: 'none', cursor: copyRunning ? 'not-allowed' : 'pointer',
                    background: '#10b981', color: '#fff', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500
                  }}
                >
                  <Upload size={14} /> {copyRunning ? 'Copying...' : `Copy ${copyDryRun.blobCount} files to SharePoint`}
                </button>
              )}
            </div>
          )}

          {copyDryRun && (
            <div style={{ padding: 12, background: copyDryRun.success ? '#f0f9ff' : '#fef2f2', border: `1px solid ${copyDryRun.success ? '#bfdbfe' : '#fecaca'}`, borderRadius: 8, marginBottom: 12 }}>
              {copyDryRun.success ? (
                <>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8, color: 'var(--idx-text-primary)' }}>
                    <Database size={14} style={{ display: 'inline', marginRight: 6 }} />
                    {copyDryRun.blobCount} files ({copyDryRun.totalSizeMB} MB) in {copyDryRun.containerName}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--idx-text-muted)', marginBottom: 8 }}>
                    Target: AskAldoKB / {copyDryRun.targetFolder}
                  </div>
                  {copyDryRun.blobs && (
                    <div style={{ maxHeight: 200, overflow: 'auto', fontSize: 12 }}>
                      {copyDryRun.blobs.map((b: any, i: number) => (
                        <div key={i} style={{ padding: '2px 0', color: 'var(--idx-text-secondary)' }}>
                          {b.name} <span style={{ color: 'var(--idx-text-faint)' }}>({b.sizeMB} MB)</span>
                        </div>
                      ))}
                      {copyDryRun.truncated && <div style={{ padding: '4px 0', color: 'var(--idx-text-faint)', fontStyle: 'italic' }}>...and more</div>}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ color: '#991b1b' }}>
                  <AlertTriangle size={14} style={{ display: 'inline', marginRight: 6 }} />
                  {copyDryRun.error}
                </div>
              )}
            </div>
          )}

          {copyResult && (
            <div style={{ padding: 12, background: copyResult.success ? '#ecfdf5' : '#fef2f2', border: `1px solid ${copyResult.success ? '#a7f3d0' : '#fecaca'}`, borderRadius: 8 }}>
              {copyResult.success ? (
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#059669' }}>
                    <CheckCircle size={14} style={{ display: 'inline', marginRight: 6 }} />
                    Copy complete: {copyResult.copied} files copied, {copyResult.failed} failed
                  </div>
                  {copyResult.errors?.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#991b1b' }}>
                      {copyResult.errors.map((e: any, i: number) => (
                        <div key={i}>{e.blob}: {e.error}</div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ color: '#991b1b' }}>
                  <AlertTriangle size={14} style={{ display: 'inline', marginRight: 6 }} />
                  {copyResult.error}
                </div>
              )}
            </div>
          )}

          {/* Agent-folder mapping reference */}
          {!copyAgent && (
            <div style={{ marginTop: 16 }}>
              <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--idx-text-primary)' }}>
                <MapPin size={14} /> Agent to SharePoint Folder Mapping
              </h4>
              <div style={{ display: 'grid', gap: 8 }}>
                {Object.entries(AGENT_BLOB_MAPPING).map(([agentId, mapping]) => (
                  <div key={agentId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--idx-bg-secondary)', border: '1px solid var(--idx-border)', borderRadius: 6, fontSize: 13 }}>
                    <Bot size={14} style={{ color: getAgentColor(agentId) }} />
                    <span style={{ fontWeight: 500, minWidth: 120, color: 'var(--idx-text-primary)' }}>{getAgentDisplayName(agentId)}</span>
                    <ChevronRight size={12} style={{ color: 'var(--idx-text-faint)' }} />
                    <Folder size={14} style={{ color: '#f59e0b' }} />
                    <span style={{ color: 'var(--idx-text-muted)' }}>AskAldoKB / {mapping.sharepointFolder}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--idx-text-faint)' }}>
                      {mapping.containers.map(c => c.name).join(', ')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ================================================================= */}
      {/* Change Detection Mode (Pending Changes + Approvals)                */}
      {/* ================================================================= */}
      {kbMode === 'changes' && (
        <ChangeDetectionPanel
          selectedEnvironment={selectedEnvironment}
          onRefreshIndexes={onRefreshIndexes}
        />
      )}

      {/* Inline Index Test Panel — slide-out triggered from Test Index button */}
      {testingBatch && (
        <IndexTestPanel
          batch={testingBatch}
          onClose={() => setTestingBatch(null)}
          onPromote={(batch) => {
            const aliasName = registryAgents.find(a => a.id === batch.agentId)
              ?.knowledgeBase?.indexName?.replace(/\{env\}/g, selectedEnvironment) || '';
            if (aliasName && batch.indexName) {
              handlePromote(batch.indexName, aliasName);
            }
            setTestingBatch(null);
          }}
          onReject={() => setTestingBatch(null)}
        />
      )}
    </div>
  );
};

export { KnowledgeBaseTab };
